import { TextButton, validators } from '@approvalmax/ui';
import { State } from 'modules/data';
import React, { FC, memo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { routingService } from 'services/routing';
import { getPath, Path } from 'urlBuilder';

import { login } from '../../../../actions';
import EmailField from '../../../../components/EmailField/EmailField';
import PasswordField from '../../../../components/PasswordField/PasswordField';
import { LoginModuleData } from '../../../../reducers/page/moduleReducer';
import { getAuthCommand } from '../../../../selectors/commandSelectors';
import { getModule } from '../../../../selectors/pageSelectors';
import { messages } from './LoginModule.messages';
import { Buttons, Card, Container, InvisibleSubmit, LoginButton, StyledPasskeyButton } from './LoginModule.styles';
import { LoginModuleProps } from './LoginModule.types';

const LoginModule: FC<LoginModuleProps> = memo((props) => {
    const { onBlink, setEmail } = props;

    const emailInputRef = useRef<HTMLInputElement>(null);

    const authCommand = useSelector((state: State) => getAuthCommand(state));
    const module = useSelector((state: State) => getModule<LoginModuleData>(state));

    const [loginText, setLoginText] = useState(module.email || '');
    const [loginTouched, setLoginTouched] = useState(false);
    const [password, setPassword] = useState('');
    const [passwordTouched, setPasswordTouched] = useState(false);

    const dispatch = useDispatch();

    useEffect(() => {
        setTimeout(() => {
            if (emailInputRef.current) {
                emailInputRef.current.focus();
            }
        }, 20);
    }, []);

    const showPasswordReset = () => {
        onBlink(() => {
            const encodedEmail = encodeURIComponent(getNormalisedEmail());

            routingService.push(getPath(Path.resetPassword, encodedEmail));
        });
    };

    const validateEmail = () => {
        let emailValidationError;

        const email = getNormalisedEmail();

        if (validators.required(email)) {
            emailValidationError = messages.emailRequired;
        } else if (validators.email(email)) {
            emailValidationError = messages.emailInvalid;
        }

        return emailValidationError;
    };

    const validatePassword = () => {
        let passwordValidationError;

        if (validators.required(password)) {
            passwordValidationError = messages.passwordRequired;
        }

        return passwordValidationError;
    };

    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        logIn();
    };

    const onChangeLogin = (value: string) => {
        setLoginText(value.trim());
        setEmail(value.trim());
    };

    const onChangePassword = (value: string) => {
        setPassword(value);
    };

    const logIn = () => {
        setLoginTouched(true);
        setPasswordTouched(true);

        if (validateEmail() || validatePassword()) {
            return;
        }

        dispatch(
            login({
                email: getNormalisedEmail(),
                password: password,
                app: module.app,
                postAction: module.postAction,
            })
        );
    };

    const getNormalisedEmail = () => {
        return loginText.trim();
    };

    let emailValidationError;

    if (loginTouched) {
        emailValidationError = validateEmail();
    }

    let passwordValidationError;

    if (passwordTouched) {
        passwordValidationError = validatePassword();
    }

    return (
        <Container onSubmit={onSubmit} autoComplete='on' data-qa='login-page__form'>
            <InvisibleSubmit type='submit' />

            <Card>
                <EmailField
                    ref={emailInputRef}
                    value={loginText}
                    onChange={onChangeLogin}
                    validationError={emailValidationError}
                />

                <PasswordField value={password} onChange={onChangePassword} validationError={passwordValidationError} />
            </Card>

            <Buttons>
                <TextButton
                    size='small1'
                    colorTheme='dark-green'
                    upperCased={false}
                    underlined={true}
                    command={authCommand}
                    execute={showPasswordReset}
                >
                    {messages.recoverPasswordButtonText}
                </TextButton>

                <StyledPasskeyButton />

                <LoginButton qa='login-page__log-in-btn' disabled={authCommand.disabled} execute={logIn}>
                    {messages.loginButtonText}
                </LoginButton>
            </Buttons>
        </Container>
    );
});

export default LoginModule;
